/**
 * Candidate-facing quiz — token link, no sign-in, tablet-first, TIMED.
 * Flow: welcome → English → Writing → Maths → check & submit → thank you.
 * Timer parity with the old Microsoft Forms quiz: limit from settings
 * (default 35 min), countdown starts at Start, final-minute warning, answers
 * auto-submit at zero. Server refuses submissions past the limit + grace.
 * Answers kept in memory with a best-effort localStorage backup so an
 * accidental refresh resumes with the clock still running.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../api/client";
import type { QuizPayload, QuizQuestion } from "../../api/types";

type Answers = Record<number, string>;

const SECTION_TITLES: Record<string, string> = {
  english: "English",
  writing: "Writing",
  maths: "Maths",
};

function draftKey(token: string) {
  return `liaise-assessment-draft-${token.slice(0, 12)}`;
}

/** remaining ms from server-reported start/now plus the local clock. */
function remainingMs(limitMin: number, startedAtIso: string, serverNowIso: string): number {
  const elapsed = new Date(serverNowIso).getTime() - new Date(startedAtIso).getTime();
  return limitMin * 60_000 - elapsed;
}

export default function TakePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [quiz, setQuiz] = useState<QuizPayload | null>(null);
  const [loadError, setLoadError] = useState("");
  const [stage, setStage] = useState<"welcome" | number | "review" | "done" | "timeup">("welcome");
  const [answers, setAnswers] = useState<Answers>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [deadlineAt, setDeadlineAt] = useState<number | null>(null);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [startError, setStartError] = useState("");

  // Tiled identifying watermark over the paper. A deterrent: it ties any capture
  // to this sitting (name + Test ID + time); it cannot stop capture itself.
  const wmUrl = useMemo(() => {
    const wm = quiz?.watermark;
    if (!wm?.enabled || !wm.text) return null;
    const stamp = new Date();
    const txt = `${wm.text} · ${stamp.toLocaleDateString("en-GB")} ${stamp.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    const esc = txt
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/'/g, "&apos;")
      .replace(/"/g, "&quot;");
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='440' height='230'><text x='220' y='115' text-anchor='middle' transform='rotate(-24 220 115)' font-family='Open Sans,Arial,sans-serif' font-size='15' fill='#173C69' fill-opacity='0.08'>${esc}</text></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }, [quiz]);
  const answersRef = useRef<Answers>({});
  answersRef.current = answers;

  // --- integrity telemetry (disclosed in the consent notice) ---
  const focusLossRef = useRef(0);
  const pasteCountRef = useRef(0);
  const queueRef = useRef<Array<{ kind: string; detail?: Record<string, string | number | boolean> }>>([]);
  const inTestRef = useRef(false);

  const queueEvent = (kind: string, detail?: Record<string, string | number | boolean>) => {
    queueRef.current.push({ kind, detail });
  };

  useEffect(() => {
    const flush = () => {
      if (queueRef.current.length === 0) return;
      const batch = queueRef.current.splice(0, 20);
      void api.candidate.postEvents(token, batch);
    };
    const iv = setInterval(flush, 5000);

    const onVis = () => {
      if (!inTestRef.current) return;
      if (document.hidden) {
        focusLossRef.current += 1;
        queueEvent("focus_lost", { n: focusLossRef.current });
      } else {
        queueEvent("focus_gained", {});
      }
    };
    const onBlur = () => {
      if (!inTestRef.current || document.hidden) return;
      focusLossRef.current += 1;
      queueEvent("focus_lost", { n: focusLossRef.current, via: "blur" });
    };

    // --- capture deterrence (logged, disclosed; cannot truly block OS capture) ---
    const inFormField = (e: Event) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT");
    };
    const onCopyCut = (e: ClipboardEvent) => {
      if (!inTestRef.current || inFormField(e)) return; // typing their own answer stays normal
      e.preventDefault();
      queueEvent("copy_blocked", { via: e.type });
    };
    const onCtxMenu = (e: MouseEvent) => {
      if (!inTestRef.current || inFormField(e)) return;
      e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      // best effort: Windows browsers surface the key; macOS capture shortcuts never reach the page
      if (!inTestRef.current || e.key !== "PrintScreen") return;
      queueEvent("screenshot_key", {});
      window.dispatchEvent(new CustomEvent("liaise-capture-notice"));
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("copy", onCopyCut);
    document.addEventListener("cut", onCopyCut);
    document.addEventListener("contextmenu", onCtxMenu);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("copy", onCopyCut);
      document.removeEventListener("cut", onCopyCut);
      document.removeEventListener("contextmenu", onCtxMenu);
      document.removeEventListener("keyup", onKeyUp);
      flush();
    };
  }, [token]);

  // brief on-screen notice when a PrintScreen key press is seen
  const [captureNotice, setCaptureNotice] = useState(false);
  useEffect(() => {
    let t: number | undefined;
    const show = () => {
      setCaptureNotice(true);
      window.clearTimeout(t);
      t = window.setTimeout(() => setCaptureNotice(false), 4000);
    };
    window.addEventListener("liaise-capture-notice", show);
    return () => {
      window.removeEventListener("liaise-capture-notice", show);
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    api.candidate
      .get(token)
      .then((q) => {
        setQuiz(q);
        let saved: Answers = {};
        try {
          const raw = localStorage.getItem(draftKey(token));
          if (raw) saved = JSON.parse(raw);
        } catch {
          /* storage unavailable */
        }
        setAnswers(saved);
        if (q.status === "submitted") {
          setStage("done");
          return;
        }
        // Resume a started sitting with the clock still running
        const limit = q.time_limit_minutes ?? 0;
        if (q.status === "in_progress" && q.started_at && q.server_now) {
          if (limit > 0) {
            const rem = remainingMs(limit, q.started_at, q.server_now);
            if (rem <= 0) {
              setStage("timeup");
              return;
            }
            setDeadlineAt(Date.now() + rem);
          }
          inTestRef.current = true;
          void api.candidate.postEvents(token, [{ kind: "resumed" }]);
          setStage(0);
        }
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "This link is not valid."));
  }, [token]);

  useEffect(() => {
    try {
      if (Object.keys(answers).length) localStorage.setItem(draftKey(token), JSON.stringify(answers));
    } catch {
      /* storage unavailable */
    }
  }, [answers, token]);

  const sections = quiz?.sections ?? [];
  const allQuestions = useMemo(() => sections.flatMap((s) => s.questions), [sections]);
  const answeredCount = allQuestions.filter((q) => (answers[q.id] ?? "").trim() !== "").length;
  const limitMin = quiz?.time_limit_minutes ?? 0;

  const start = async () => {
    setStartError("");
    try {
      const r = await api.candidate.start(token, {
        consent: true,
        declaration: true,
        client: {
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
          locale: navigator.language ?? "",
          screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
          platform: navigator.platform ?? "",
        },
      });
      if ((r.time_limit_minutes ?? 0) > 0) {
        setDeadlineAt(Date.now() + remainingMs(r.time_limit_minutes, r.started_at, r.server_now));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("Assessment closed") || msg.includes("Consent required")) {
        setStartError(msg.replace(/^\d+:\s*/, ""));
        return;
      }
      // non-fatal network blip: fall back to a locally-started clock
      if (limitMin > 0) setDeadlineAt(Date.now() + limitMin * 60_000);
    }
    inTestRef.current = true;
    setStage(0);
  };

  const submit = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const a = answersRef.current;
      await api.candidate.submit(
        token,
        allQuestions.map((q) => ({ question_id: q.id, answer: (a[q.id] ?? "").trim() || null })),
        { focus_loss_count: focusLossRef.current, paste_count: pasteCountRef.current },
      );
      inTestRef.current = false;
      try {
        localStorage.removeItem(draftKey(token));
      } catch {
        /* ignore */
      }
      setAutoSubmitted(auto);
      setStage("done");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("Time is up")) setStage("timeup");
      else setSubmitError(msg.replace(/^\d+:\s*/, "") || "Something went wrong - please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------------------------------------- frames */

  if (loadError) {
    return (
      <Frame>
        <h1 className="text-2xl font-bold text-liaise-navy">Sorry, this link isn't working</h1>
        <p className="text-slate-600 mt-2">{loadError}</p>
        <p className="text-sm text-slate-400 mt-4">Please ask the Liaise team member who set up your assessment for a new link.</p>
      </Frame>
    );
  }

  if (!quiz) {
    return (
      <Frame>
        <p className="text-slate-500">Loading your assessment...</p>
      </Frame>
    );
  }

  if (stage === "timeup") {
    return (
      <Frame>
        <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center">
          <span className="text-liaise-orange text-3xl">⏱</span>
        </div>
        <h1 className="text-2xl font-bold text-liaise-navy mt-4">Time is up</h1>
        <p className="text-slate-600 mt-2">
          The time limit for this assessment has passed. Please hand the device back to the Liaise team member - they can
          issue a new link if a retake is agreed.
        </p>
      </Frame>
    );
  }

  if (stage === "done") {
    return (
      <Frame>
        <div className="w-16 h-16 mx-auto rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
          <span className="text-liaise-green text-3xl">✓</span>
        </div>
        <h1 className="text-2xl font-bold text-liaise-navy mt-4">Thank you{quiz.first_name ? `, ${quiz.first_name}` : ""}!</h1>
        <p className="text-slate-600 mt-2">
          {autoSubmitted ? "Time was up, so your answers were submitted automatically. " : ""}
          {(quiz.thankyou_text ?? "Your assessment has been submitted. Please inform the manager that you have finished.")
            .replace("{name}", quiz.first_name ?? "")
            .replace(/^Thank you[^!.]*[!.]\s*/i, "") /* heading already says it */}
        </p>
      </Frame>
    );
  }

  if (stage === "welcome") {
    // outside the admin-defined opening hours, an unstarted sitting sees only the schedule
    if (quiz.window && quiz.window.enabled && !quiz.window.open && quiz.status === "created") {
      return (
        <Frame>
          <div className="w-16 h-16 mx-auto rounded-full bg-liaise-navy-50 border border-liaise-navy-200 flex items-center justify-center">
            <span className="text-liaise-navy text-3xl">🕒</span>
          </div>
          <h1 className="text-2xl font-bold text-liaise-navy mt-4">The assessment is closed right now</h1>
          <p className="text-slate-600 mt-2">
            It can be taken: <strong>{quiz.window.description}</strong>. Please come back then, or speak to the Liaise
            team member who gave you this link.
          </p>
        </Frame>
      );
    }

    const canStart = consent && declaration;
    return (
      <Frame wide>
        <h1 className="text-3xl font-bold text-liaise-navy">
          Welcome{quiz.first_name ? `, ${quiz.first_name}` : ""}
        </h1>
        <p className="text-slate-600 mt-3 text-lg leading-relaxed">
          This is the Liaise recruitment assessment. It has {sections.length} short sections
          {sections.length ? ` - ${sections.map((s) => SECTION_TITLES[s.name] ?? s.name).join(", ")}` : ""} with{" "}
          {allQuestions.length} questions in total.
        </p>
        <ul className="text-slate-600 mt-4 space-y-2 text-left max-w-xl mx-auto list-disc pl-6">
          {limitMin > 0 ? (
            <li>
              <strong>This is a timed assessment: {limitMin} minutes.</strong> The timer starts when you press Start and
              can't be paused. Your answers are submitted automatically when the time is up.
            </li>
          ) : (
            <li>Take your time - there's no strict time limit.</li>
          )}
          <li>You can move back and forward between sections before you finish.</li>
          <li>Press <strong>Submit</strong> at the end when you're happy with your answers.</li>
        </ul>

        {quiz.privacy_notice && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-6 text-left max-h-44 overflow-y-auto">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Privacy notice</p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{quiz.privacy_notice}</p>
          </div>
        )}

        <div className="text-left max-w-xl mx-auto mt-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-700">
            <input type="checkbox" className="mt-1 accent-[#173C69]" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I have read the privacy notice and agree to the information described being recorded.</span>
          </label>
          {quiz.declaration && (
            <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-700">
              <input type="checkbox" className="mt-1 accent-[#173C69]" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
              <span>{quiz.declaration}</span>
            </label>
          )}
        </div>

        {startError && <p className="text-liaise-red mt-4">{startError}</p>}

        <button className="btn-primary text-lg px-8 py-3 mt-6" onClick={start} disabled={!canStart}>
          {limitMin > 0 ? `Start the ${limitMin}-minute assessment` : "Start the assessment"}
        </button>
        {!canStart && <p className="text-xs text-slate-400 mt-2">Tick both boxes to start.</p>}
      </Frame>
    );
  }

  if (stage === "review") {
    const unanswered = allQuestions.filter((q) => (answers[q.id] ?? "").trim() === "");
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <QuizHeader label="Check & submit" deadlineAt={deadlineAt} onExpire={() => submit(true)} />
        <Watermark url={wmUrl} />
        <CaptureNotice show={captureNotice} />
        <main className="flex-1 w-full max-w-2xl mx-auto p-5 pb-10 space-y-5">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 text-center">
            <h1 className="text-2xl font-bold text-liaise-navy">Check your answers</h1>
            <p className="text-slate-600 mt-2">
              You've answered <strong>{answeredCount}</strong> of <strong>{allQuestions.length}</strong> questions.
              Review the summary below, then submit.
            </p>
            {unanswered.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4 text-left">
                <p className="text-sm font-semibold text-orange-700">Not answered yet:</p>
                <p className="text-sm text-orange-700 mt-1">
                  {unanswered.map((q) => `Q${q.display_no ?? q.q_no}`).join(", ")} - blank answers score zero.
                </p>
              </div>
            )}
          </div>

          {sections.map((s, sIdx) => (
            <div key={s.name} className="card overflow-hidden">
              <div className="bg-liaise-navy text-white px-4 py-2 text-sm font-semibold flex items-center justify-between">
                <span className="capitalize">{SECTION_TITLES[s.name] ?? s.name}</span>
                <button className="text-xs underline text-white/80 hover:text-white" onClick={() => { setStage(sIdx); window.scrollTo(0, 0); }}>
                  Change answers
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {s.questions.map((q) => {
                  const v = (answers[q.id] ?? "").trim();
                  return (
                    <div key={q.id} className="px-4 py-2.5 text-sm flex gap-3">
                      <span className="font-bold text-liaise-navy shrink-0">Q{q.display_no ?? q.q_no}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-slate-500 truncate">{q.question}</p>
                        <p className={`mt-0.5 whitespace-pre-wrap break-words ${v ? "text-slate-800 font-medium" : "text-liaise-orange font-semibold"}`}>
                          {v ? fmtCandidateAnswer(v) : "Not answered"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
            {submitError && <p className="text-liaise-red mb-3">{submitError}</p>}
            <div className="flex justify-center gap-3 flex-wrap">
              <button className="btn-secondary" onClick={() => setStage(sections.length - 1)}>← Go back</button>
              <button className="btn-primary text-lg px-8" onClick={() => submit(false)} disabled={submitting}>
                {submitting ? "Submitting..." : "Submit my answers"}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* section stage */
  const sIdx = stage as number;
  const section = sections[sIdx];
  if (!section) return null;
  const sectionAnswered = section.questions.filter((q) => (answers[q.id] ?? "").trim() !== "").length;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <QuizHeader
        label={`${SECTION_TITLES[section.name] ?? section.name} · section ${sIdx + 1} of ${sections.length}`}
        progressPct={Math.round((answeredCount / Math.max(allQuestions.length, 1)) * 100)}
        deadlineAt={deadlineAt}
        onExpire={() => submit(true)}
      />
      <Watermark url={wmUrl} />
      <CaptureNotice show={captureNotice} />

      <main className="flex-1 max-w-3xl mx-auto w-full px-5 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-liaise-navy">{SECTION_TITLES[section.name] ?? section.name}</h1>
          <p className="text-sm text-slate-500">
            {sectionAnswered} of {section.questions.length} answered in this section
          </p>
        </div>

        {section.questions.map((q) => (
          <QuestionCard
            key={q.id}
            q={q}
            value={answers[q.id] ?? ""}
            onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
            onPaste={(chars) => {
              pasteCountRef.current += 1;
              queueEvent("paste", { q: q.q_no, chars });
            }}
          />
        ))}

        <div className="flex justify-between pt-4 pb-10">
          {sIdx > 0 ? (
            <button className="btn-secondary" onClick={() => { setStage(sIdx - 1); window.scrollTo(0, 0); }}>← {SECTION_TITLES[sections[sIdx - 1].name]}</button>
          ) : (
            <span />
          )}
          {sIdx < sections.length - 1 ? (
            <button className="btn-primary" onClick={() => { setStage(sIdx + 1); window.scrollTo(0, 0); }}>{SECTION_TITLES[sections[sIdx + 1].name]} →</button>
          ) : (
            <button className="btn-primary" onClick={() => { setStage("review"); window.scrollTo(0, 0); }}>Check & submit →</button>
          )}
        </div>
      </main>
    </div>
  );
}

/** Multi-part answers are stored as JSON arrays; show them readably. */
function fmtCandidateAnswer(v: string): string {
  if (v.startsWith("[")) {
    try {
      const arr = JSON.parse(v);
      if (Array.isArray(arr)) return arr.filter((x) => String(x).trim() !== "").join(", ");
    } catch {
      /* fall through */
    }
  }
  return v;
}

/* ------------------------------------------------- capture deterrence UI */

function Watermark({ url }: { url: string | null }) {
  if (!url) return null;
  return <div aria-hidden className="fixed inset-0 pointer-events-none select-none z-40" style={{ backgroundImage: url }} />;
}

function CaptureNotice({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-liaise-navy text-white text-sm px-5 py-2.5 rounded-full shadow-lg">
      Screen captures are logged. This page carries your name and Test ID.
    </div>
  );
}

/* ------------------------------------------------------------- countdown */

function QuizHeader({
  label,
  progressPct,
  deadlineAt,
  onExpire,
}: {
  label: string;
  progressPct?: number;
  deadlineAt: number | null;
  onExpire: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  const expiredRef = useRef(false);

  useEffect(() => {
    if (deadlineAt == null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [deadlineAt]);

  useEffect(() => {
    if (deadlineAt != null && now >= deadlineAt && !expiredRef.current) {
      expiredRef.current = true;
      onExpire();
    }
  }, [now, deadlineAt, onExpire]);

  const rem = deadlineAt != null ? Math.max(0, deadlineAt - now) : null;
  const mins = rem != null ? Math.floor(rem / 60_000) : 0;
  const secs = rem != null ? Math.floor((rem % 60_000) / 1000) : 0;
  const finalMinute = rem != null && rem <= 60_000;
  const danger = rem != null && rem <= 5 * 60_000;
  const warn = rem != null && rem <= 10 * 60_000 && !danger;

  // one-time "X minutes remaining" banners so nobody loses track of the clock
  const [milestone, setMilestone] = useState<string | null>(null);
  const shownRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (rem == null) return;
    for (const m of [10, 5]) {
      if (rem <= m * 60_000 && rem > (m - 1) * 60_000 && !shownRef.current.has(m)) {
        shownRef.current.add(m);
        setMilestone(`${m} minutes remaining`);
        const t = setTimeout(() => setMilestone(null), 12_000);
        return () => clearTimeout(t);
      }
    }
  }, [rem]);

  // fraction of the sitting's time already used, for the drain bar
  const totalRef = useRef<number | null>(null);
  if (rem != null && (totalRef.current == null || rem > totalRef.current)) totalRef.current = rem;
  const usedPct = rem != null && totalRef.current ? Math.min(100, 100 - (rem / totalRef.current) * 100) : null;

  return (
    <header className="bg-liaise-navy text-white sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-5 py-2.5 flex items-center justify-between gap-3">
        <span className="font-bold text-lg">liaise</span>
        <span className="text-sm text-white/80 truncate">{label}</span>
        {rem != null && (
          <span
            className={`font-mono text-lg font-bold px-3.5 py-1.5 rounded-xl tabular-nums shadow-sm ${
              finalMinute
                ? "bg-liaise-red text-white animate-pulse"
                : danger
                  ? "bg-liaise-red text-white"
                  : warn
                    ? "bg-liaise-orange text-white"
                    : "bg-white text-liaise-navy"
            }`}
            title="Time remaining"
          >
            ⏱ {mins}:{String(secs).padStart(2, "0")}
          </span>
        )}
      </div>
      {usedPct != null && (
        <div className="h-1.5 bg-white/20" aria-hidden>
          <div
            className={`h-1.5 transition-all ${danger ? "bg-liaise-red" : warn ? "bg-liaise-orange" : "bg-liaise-green"}`}
            style={{ width: `${100 - usedPct}%` }}
            title="Time remaining"
          />
        </div>
      )}
      {progressPct != null && (
        <div className="h-1 bg-white/10">
          <div className="h-1 bg-liaise-yellow transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      )}
      {finalMinute ? (
        <div className="bg-liaise-red text-white text-center text-sm font-semibold py-1.5">
          Final minute - your answers will be submitted automatically when the timer ends.
        </div>
      ) : milestone ? (
        <div className={`${danger ? "bg-liaise-red" : "bg-liaise-orange"} text-white text-center text-sm font-semibold py-1.5`}>
          {milestone} - keep an eye on the clock at the top
        </div>
      ) : null}
    </header>
  );
}

function QuestionCard({
  q,
  value,
  onChange,
  onPaste,
}: {
  q: QuizQuestion;
  value: string;
  onChange: (v: string) => void;
  onPaste?: (chars: number) => void;
}) {
  return (
    <div className="card p-5">
      {q.passage && (
        <div className="bg-liaise-navy-50 border border-liaise-navy-200 rounded-lg p-4 mb-4 text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap select-none">
          {q.passage}
        </div>
      )}
      <p className="text-base text-slate-800 leading-relaxed select-none">
        <span className="font-bold text-liaise-navy mr-2">Q{q.display_no ?? q.q_no}.</span>
        {q.question}
      </p>
      <div className="mt-4">
        {q.kind === "ranking" && q.options?.length ? (
          <RankingInput items={q.options} value={value} onChange={onChange} />
        ) : q.kind === "multi_select" && q.options?.length ? (
          <MultiSelectInput options={q.options} maxSelect={q.max_select ?? 3} value={value} onChange={onChange} />
        ) : q.kind === "multi_text" && q.options?.length ? (
          <MultiTextInput labels={q.options} value={value} onChange={onChange} />
        ) : q.kind === "mcq" && q.options ? (
          <div className="grid gap-2">
            {q.options.map((opt) => (
              <label
                key={opt}
                className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition text-base ${
                  value === opt ? "border-liaise-navy bg-liaise-navy-50 font-semibold" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name={`q-${q.id}`}
                  className="accent-[#173C69]"
                  checked={value === opt}
                  onChange={() => onChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        ) : q.kind === "writing" ? (
          <textarea
            className="input min-h-[220px] text-base leading-relaxed"
            placeholder="Write your answer here..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={(e) => onPaste?.(e.clipboardData?.getData("text")?.length ?? 0)}
          />
        ) : (
          <input
            className="input text-base py-3"
            inputMode={q.kind === "numeric" ? "decimal" : "text"}
            placeholder="Your answer"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Put-in-order question. The chosen sequence is stored comma-joined.
 * Tap an item to add it as the next position; tap a chosen item to remove it.
 */
function RankingInput({ items, value, onChange }: { items: string[]; value: string; onChange: (v: string) => void }) {
  const chosen = value ? value.split(",").map((s) => s.trim()).filter((s) => items.includes(s)) : [];
  const remaining = items.filter((i) => !chosen.includes(i));

  return (
    <div className="space-y-3">
      <div className="min-h-[52px] border-2 border-dashed border-liaise-navy-200 rounded-lg p-2 flex flex-wrap gap-2 items-center">
        {chosen.length === 0 && <span className="text-sm text-slate-400 px-2">Tap the items below in the right order</span>}
        {chosen.map((item, i) => (
          <button
            key={item}
            type="button"
            onClick={() => onChange(chosen.filter((c) => c !== item).join(", "))}
            className="flex items-center gap-2 bg-liaise-navy text-white rounded-lg px-3 py-2 text-sm font-semibold"
            title="Tap to remove"
          >
            <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs">{i + 1}</span>
            {item}
            <span aria-hidden>×</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {remaining.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onChange([...chosen, item].join(", "))}
            className="border border-slate-300 hover:border-liaise-navy rounded-lg px-3 py-2 text-sm bg-white"
          >
            {item}
          </button>
        ))}
        {chosen.length > 0 && (
          <button type="button" onClick={() => onChange("")} className="text-sm text-liaise-red hover:underline px-2">
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

/** Pick exactly N options (stored as a JSON array) — e.g. the 3 correct spellings. */
function MultiSelectInput({
  options,
  maxSelect,
  value,
  onChange,
}: {
  options: string[];
  maxSelect: number;
  value: string;
  onChange: (v: string) => void;
}) {
  let chosen: string[] = [];
  try {
    const parsed = value ? JSON.parse(value) : [];
    if (Array.isArray(parsed)) chosen = parsed.map((p) => String(p)).filter((p) => options.includes(p));
  } catch {
    chosen = [];
  }

  const toggle = (opt: string) => {
    const next = chosen.includes(opt)
      ? chosen.filter((c) => c !== opt)
      : chosen.length < maxSelect
        ? [...chosen, opt]
        : chosen;
    onChange(JSON.stringify(next));
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Choose {maxSelect} ({chosen.length} of {maxSelect} selected)
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((opt) => {
          const selected = chosen.includes(opt);
          const disabled = !selected && chosen.length >= maxSelect;
          return (
            <label
              key={opt}
              className={`flex items-center gap-3 border rounded-lg px-4 py-3 transition text-base ${
                selected
                  ? "border-liaise-navy bg-liaise-navy-50 font-semibold cursor-pointer"
                  : disabled
                    ? "border-slate-100 text-slate-300 cursor-not-allowed"
                    : "border-slate-200 hover:border-slate-300 cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                className="accent-[#173C69]"
                checked={selected}
                disabled={disabled}
                onChange={() => toggle(opt)}
              />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Several short answers in one question (stored as a JSON array). */
function MultiTextInput({ labels, value, onChange }: { labels: string[]; value: string; onChange: (v: string) => void }) {
  let parts: string[] = [];
  try {
    const parsed = value ? JSON.parse(value) : [];
    if (Array.isArray(parsed)) parts = parsed.map((p) => String(p));
  } catch {
    parts = [];
  }
  while (parts.length < labels.length) parts.push("");

  const set = (i: number, v: string) => {
    const next = [...parts];
    next[i] = v;
    onChange(JSON.stringify(next));
  };

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {labels.map((label, i) => (
        <div key={label + i}>
          <label className="label">{label}</label>
          <input className="input text-base py-3" value={parts[i] ?? ""} onChange={(e) => set(i, e.target.value)} />
        </div>
      ))}
    </div>
  );
}

function Frame({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-screen bg-liaise-navy flex items-center justify-center p-5">
      <div className={`bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center w-full ${wide ? "max-w-2xl" : "max-w-lg"}`}>
        {children}
      </div>
    </div>
  );
}
